<template>
<div v-if="active">

  <ValidationObserver ref="observer" v-slot="{ invalid }">
    <div
      class="flex flex-wrap items-end pb-2 border-b border-theme-200 light:border-theme-600"
      v-if="title || closeButton"
    >
      <div class="w-1/2">
        <p class="text-xl font-bold" v-if="title">{{ title }}</p>
      </div>
      <div class="w-1/2 text-right">
        <div class="btn btn-red" @click="close" v-if="closeButton">
          <i class="fas fa-window-close"></i>
          <span class="ml-2">cancel</span>
        </div>
      </div>
    </div>

    <div v-if="amountInput">
      <ValidationProvider
        :rules="{
          required: true,
          decimal: true
        }"
        name="value"
        v-slot="{ errors }"
      >
        <div class="flex mt-4 items-center">
          <div class="h-10 px-2 py-2 rounded-l border border-r-0 border-theme-200 light:border-theme-600 focus:outline-none">
            {{ config.currencySymbol }}
          </div>
            <input
              name="value"
              type="text"
              id="value"
              class="form-control form-no-outline h-10 border-r-0 rounded-none"
              autocomplete="off"
              v-model="editor.data.value"
              @input="editor.percentage = 0"
            >
            <button
              type="button"
              class="h-10 px-5 border border-r-0 border-purple-400 light:border-purple-600 focus:outline-none hover:bg-purple-800 hover:border-purple-700"
              :class="editor.percentage === 100 ? 'bg-purple-500 text-white' : ''"
              @click="percentage(100)"
            >100%</button>
            <button
              type="button"
              class="h-10 px-5 border border-purple-400 light:border-purple-600 rounded-r focus:outline-none hover:bg-purple-800 hover:border-purple-700"
              :class="editor.percentage === 50 ? 'bg-purple-500 text-white' : ''"
              @click="percentage(50)"
            >50%</button>
        </div>
        <span class="text-sm text-red-500">{{ errors[0] }}</span>
      </ValidationProvider>
    </div>

    <div v-else class="text-center py-4 border-b border-theme-200 light:border-theme-600">
      <p class="text-4xl font-bold leading-none">{{ $formatMoney(editor.data.value) }}</p>
      <p v-if="editor.data.value !== amount">of {{ $formatMoney(amount) }}</p>
    </div>

    <div class="flex flex-wrap mt-4 -mx-1 lg:-mx-2 text-xs md:text-sm lg:text-base">
      <div class="w-1/3 px-1 lg:px-2">
        <button
          type="button"
          class="btn w-full px-3 py-2 focus:outline-none"
          :class="editor.data.method === 'cash' ? 'btn-purple' : 'border border-theme-200 light:border-theme-600'"
          @click="selectMethod('cash')"
        >Cash</button>
      </div>
      <div class="w-1/3 px-1 lg:px-2">
        <button
          type="button"
          class="btn w-full px-3 py-2 text-gray-700focus:outline-none"
          :class="editor.data.method === 'credit' ? 'btn-purple' : 'border border-theme-200 light:border-theme-600'"
          @click="selectMethod('credit')"
        >Credit card</button>
      </div>
      <div class="w-1/3 px-1 lg:px-2">
        <button
          type="button"
          class="btn w-full px-3 py-2 focus:outline-none"
          :class="editor.data.method === 'cheque' ? 'btn-purple' : 'border border-theme-200 light:border-theme-600'"
          @click="selectMethod('cheque')"
        >Cheque</button>
      </div>
    </div>

    <div class="mt-4" v-if="editor.data.method === 'cheque'">
      <p class="text-lg font-bold">Cheque</p>

      <div class="flex flex-wrap -mx-2">
        <div class="w-full lg:w-1/2 mb-1 px-2">
          <label for="financial-account">
            Bank name
            <span class="text-red-500">*</span>
          </label>
          <ValidationProvider
            rules="required"
            name="cheque bank name"
            v-slot="{ errors }"
          >
            <input
              name="bank_name"
              type="text"
              id="bank-name"
              class="form-control"
              autocomplete="off"
              v-model="editor.data.cheque.bank_name"
            >
            <span class="text-sm text-red-500">{{ errors[0] }}</span>
          </ValidationProvider>
        </div>

        <div class="w-full lg:w-1/2 mb-1 px-2">
          <label for="financial-account">
            Cheque number
            <span class="text-red-500">*</span>
          </label>
          <ValidationProvider
            rules="required"
            name="cheque number"
            v-slot="{ errors }"
          >
            <input
              name="cheque_number"
              type="text"
              id="cheque-number"
              class="form-control"
              autocomplete="off"
              v-model="editor.data.cheque.cheque_number"
            >
            <span class="text-sm text-red-500">{{ errors[0] }}</span>
          </ValidationProvider>
        </div>

        <div class="w-full lg:w-1/2 mb-1 px-2">
          <label for="financial-account">
            Cheque date
            <span class="text-red-500">*</span>
          </label>
          <ValidationProvider
            rules="required|date_format:DD/MM/YYYY"
            name="cheque date"
            v-slot="{ errors }"
          >
            <input
              name="cheque_date"
              type="text"
              v-mask="'##/##/####'"
              id="cheque-date"
              class="form-control"
              placeholder="dd/mm/yyyy"
              autocomplete="off"
              v-model="editor.data.cheque.cheque_date"
            >
            <span class="text-sm text-red-500">{{ errors[0] }}</span>
          </ValidationProvider>
        </div>

        <div class="w-full lg:w-1/2 mb-1 px-2">
          <label for="financial-account">Account number</label>
          <input
            name="account_number"
            data-vv-as="account number"
            type="text"
            id="account-number"
            class="form-control"
            autocomplete="off"
            v-model="editor.data.cheque.account_number"
          >
        </div>
      </div>
    </div>

    <div class="mt-4 pt-4 border-t border-theme-200 light:border-theme-600">
      <p>Financial account</p>
      <select
        id="financial-account"
        class="form-control"
        v-model="editor.data.financial_account_id"
      >
        <option
          :value="account.id"
          v-for="(account, i) in financialAccountsForMethod"
          :key="'financial-accounts-' + i"
        >{{ account.name }} ({{ $formatMoney(account.balance) }})</option>
      </select>
      <p
        v-show="paymentType === 'expense' && selectedFinancialAccount.balance < editor.data.value"
        class="text-sm text-red-500"
      >Insufficient balance, will result in negative balance</p>
    </div>

    <div class="mt-4">
      <label for="note">Note</label>
      <textarea
        name="note"
        class="form-control"
        id="note"
        rows="3"
        v-model="editor.data.note"
      ></textarea>
    </div>

    <div class="text-right mt-4">
      <button
        class="btn btn-green px-3 py-2"
        v-if="editor.data.value != 0 && editor.data.financial_account_id"
        @click="save"
      >
        <i class="fas fa-check"></i>
        <span class="ml-2">Done</span>
      </button>
    </div>

  </ValidationObserver>

</div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  props: {
    active: Boolean,
    amount: Number,
    amountInput: {
      type: Boolean,
      default: true
    },
    payAmount: Number,
    payAmountPercentage: Number,
    paymentType: {
      type: String,
      default: 'income'
    },
    title: {
      type: String,
      default: 'New payment'
    },
    closeButton: Boolean,
  },
  data() {
    return {
      editor: {
        percentage: 0,
        data: {
          financial_account_id: null,
          value: 0,
          type: '',
          method: 'cash',
          status: 'paid',
          note: '',
          cheque: {},
        }
      }
    }
  },
  watch: {
    payAmount() {
      this.setPayAmount()
    },
    payAmountPercentage() {
      this.setPayAmount()
    },
    paymentType() {
      this.$set(this.editor.data, 'type', this.paymentType)
    },
    'editor.data': {
      handler() {
        this.$emit('input', this.editor.data)
      },
      deep: true
    },
    active() {
      if (!this.active) {
        this.close()
      }
      this.selectMethod('cash')
      this.setPayAmount()
    },
    amount() {
      this.setPayAmount()
    }
  },
  computed: {
    selectedFinancialAccount() {
      if (!this.editor.data.financial_account_id) {
        return {}
      }
      return this.config.financialAccounts.filter(a => a.id === this.editor.data.financial_account_id)[0]
    },
    financialAccountsForMethod() {
      if (this.editor.data.method === 'cheque') {
        return this.config.financialAccounts
      }
      let type = this.editor.data.method === 'credit' ? 'bank' : 'cash'
      return this.config.financialAccounts.filter(a => a.type === type)
    },
    ...mapGetters({
      config: 'config/config',
    })
  },
  mounted() {
    this.editor.data.type = this.paymentType
    this.selectMethod('cash')
    this.setPayAmount()
  },
  methods: {
    setPayAmount() {
      if (this.payAmount) {
        if (this.payAmount > this.amount) {
          console.log('payAmount cannot be greater than amount')
          this.payAmount = this.amount
        }
        this.editor.data.value = this.payAmount
      } else if (this.payAmountPercentage) {
        this.percentage(parseInt(this.payAmountPercentage))
      }
    },
    async save () {
      if (this.editor.data.value > this.amount) {
        this.$notify({type: 'error', title: `Amount cannot be higher than ${this.amount}`})
        return
      }
      const isValid = await this.$refs.observer.validate()
      if (!isValid) {
        this.$notify({type: 'error', title: 'Form has errors'})
        return
      }
      this._save()
    },
    _save() {
      // convert value to number format
      this.editor.data.value = +this.editor.data.value
      this.$emit('input', this.editor.data)
      this.$nextTick(() => {
        this.$emit('save')
      })
    },
    close() {
      this.editor.percentage = 0
      this.editor.data.value = 0
      // this.editor.data.financial_account_id = null
      // this.editor.data.method = 'cash'
      this.selectMethod('cash')
      this.editor.data.status = 'paid'
      this.editor.data.cheque = {}
      this.editor.active = false
      this.$emit('update:active', false)
    },
    percentage(value) {
      this.editor.percentage = value
      if (value === 100) {
        this.editor.data.value = this.amount
      } else {
        this.editor.data.value = Math.round((this.amount * value / 100) * 100) / 100
      }
    },
    selectMethod(method) {
      this.editor.data.method = method
      this.editor.data.financial_account_id = this.financialAccountsForMethod[0].id
    },
  }
}
</script>
